import { Label } from '@/components/ui-kit/label';
import { getNestedValue, resolveTemplateVariables } from '../utils';
import { cn } from '@/lib/utils';
import { AISuggestionStatusBadge } from './AISuggestionStatusBadge';
import { FieldRendererProps } from '../types';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
/**
 * Select Field Renderer
 * Renders select options as cards (similar to Work Arrangement step)
 */
export function SelectField({
  field,
  value,
  onChange,
  error,
  enrichmentContext,
  formData,
}: FieldRendererProps) {
  const showSuggestion = field.aiSuggestionEnabled && field.aiSuggestionKey;

  // Resolve template variables
  const resolvedLabel = resolveTemplateVariables(field.label, formData, enrichmentContext);

  const resolvedHelpText = resolveTemplateVariables(field.helpText, formData, enrichmentContext);

  // Check if we have option descriptions from enrichment context
  // Format: field.options might be simple strings, or we might have structured data
  const getOptionLabel = (option: string) => {
    // Try to get a more descriptive label from enrichment context if available
    const optionKey = `${field.name}_${option}_label`;
    if (enrichmentContext && optionKey in enrichmentContext) {
      // Apply variable interpolation to enrichment label
      return resolveTemplateVariables(
        String(enrichmentContext[optionKey]),
        formData,
        enrichmentContext
      );
    }
    // Apply variable interpolation to raw option
    return resolveTemplateVariables(option, formData, enrichmentContext);
  };

  const getOptionDescription = (option: string) => {
    const optionKey = `${field.name}_${option}_description`;
    if (enrichmentContext && optionKey in enrichmentContext) {
      // Apply variable interpolation to enrichment description
      const description = resolveTemplateVariables(
        String(enrichmentContext[optionKey]),
        formData,
        enrichmentContext
      );
      return description || null;
    }
    return null;
  };

  // Get the suggested option value
  const suggestedOption =
    showSuggestion && enrichmentContext && field.aiSuggestionKey
      ? getNestedValue(enrichmentContext, field.aiSuggestionKey)
      : null;
  const suggestedOptionStr =
    suggestedOption !== undefined && suggestedOption !== null ? String(suggestedOption) : null;

  return (
    <div className="space-y-3">
      <div className="flex  justify-between gap-4  items-start mb-2">
        <div>
          <div className="flex items-start justify-between gap-2">
            <Label className="text-base font-semibold font-nunito text-primary-light">
              {resolvedLabel}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {showSuggestion && (
              <AISuggestionStatusBadge
                suggestionKey={field.aiSuggestionKey as string}
                enrichmentContext={enrichmentContext}
                currentValue={value}
                className=" md:hidden mt-1"
              />
            )}
          </div>

          {resolvedHelpText && <p className="text-sm text-secondary-300">{resolvedHelpText}</p>}
        </div>
        {showSuggestion && (
          <AISuggestionStatusBadge
            suggestionKey={field.aiSuggestionKey as string}
            enrichmentContext={enrichmentContext}
            currentValue={value}
            className="hidden md:flex"
          />
        )}
      </div>

      {/* Pill-style option selection (segmented control) */}
      <div className="flex flex-wrap gap-2">
        {field.options.map((option) => {
          const isSelected = value === option;
          const isSuggested = suggestedOptionStr !== null && option === suggestedOptionStr;
          const hasSelection = value !== null && value !== undefined && value !== '';
          const optionLabel = getOptionLabel(option);
          const optionDescription = getOptionDescription(option);

          return (
            <motion.button
              key={option}
              type="button"
              onClick={() => onChange(field.name, option)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                'relative font-nunito inline-flex gap-2 min-h-10 items-center justify-center rounded-md border px-4 py-2 text-sm font-medium transition-all cursor-pointer',
                isSelected
                  ? 'border-selise-blue-500 bg-selise-blue-500/10 text-selise-blue-500 shadow-sm'
                  : isSuggested && !hasSelection
                    ? 'border-selise-blue-500/60 bg-selise-blue-500/5 text-selise-blue'
                    : 'border-none bg-background text-secondary-400 hover:bg-selise-blue-500/5',
                error && 'border-amber-400 bg-amber-50/50 text-amber-900 shadow-sm'
              )}
            >
              {/* Recommended badge for suggested option */}
              {isSuggested && (
                <div
                  className={cn('absolute -top-5 left-1 z-100', !hasSelection && 'animate-pulse')}
                >
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full shadow-md border',
                      'bg-white opacity-100',
                      hasSelection
                        ? 'text-selise-blue border-selise-blue/30'
                        : 'text-selise-blue border-selise-blue/40'
                    )}
                  >
                    <Sparkles className="h-2.5 w-2.5 text-selise-blue" />
                    <span className="text-selise-blue">Recommended</span>
                  </span>
                </div>
              )}

              <span
                className={cn(
                  'size-4 rounded-full flex items-center justify-center border-2 ',

                  isSelected ? 'border-selise-blue-500' : 'border-secondary-400'
                )}
              >
                {isSelected ? <span className="size-2 rounded-full bg-selise-blue-500" /> : null}
              </span>

              <div className="relative z-10 flex flex-col items-start text-left">
                <span className="whitespace-normal text-sm font-semibold">{optionLabel}</span>
                {optionDescription && (
                  <span className="mt-0.5 text-xs font-normal text-globe-grey">
                    {optionDescription}
                  </span>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {error && (
        <div className="flex items-center gap-2 mt-2 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="font-medium">{error}</span>
        </div>
      )}
    </div>
  );
}
